<template>
  <div class="finishing-options-page">
    <div class="container">
      <h1 class="page-title">CNC Machined Surface Finishes</h1>

      <div class="table-wrapper">
        <table class="finishing-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Finishing</th>
              <th>Description</th>
              <th>Application</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in finishingList" :key="index">
              <td>
                <img :src="item.image" alt="" class="process-img" />
              </td>
              <td class="process-link">{{ item.name }}</td>
              <td>
                <div>
                  <span class="process-link">Description: </span>
                  {{ item.desc }}
                </div>
                <br v-if="item.roughness" />
                <span v-if="item.roughness" class="process-link">Surface Roughness: </span>
                <span v-if="item.roughness">{{ item.roughness }}</span>
              </td>
              <td>{{ item.application }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="note-bar">
        <p class="note-text">
          *For Surface Finishes not listed here, <span class="contact-link" @click="$router.push('/contact')">please contact us directly.</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FinishingOptionsPage',
  data() {
    return {
      finishingList: [
        {
          name: 'As-Machined',
          image: require('@/assets/ma/cnc/1.png'),
          desc: 'This is the default surface finish directly from the CNC machine without any additional processing. It exhibits visible tool marks, and the roughness is usually defined by the cutting tool and the machining parameters.',
          roughness: 'Typically Ra 1.2-3.2 µm.',
          application: 'Prototypes, functional parts where aesthetics aren’t critical.'
        },
        {
          name: 'Smooth',
           image: require('@/assets/ma/cnc/2.jpg'),
          desc: 'Smoothing involves using abrasive tools or media to gently remove the top layer of material, reducing roughness and imperfections. This process can be done manually or with automated equipment depending on the complexity of the part.',
          roughness: 'Typically Ra ≤ 0.4 µm, though it can vary depending on the material and the desired finish.',
          application: 'Used for parts that need a refined appearance or smoother contact surfaces, such as consumer goods, mechanical components, and medical devices.'
        },
        {
          name: 'Bead Blasting',
           image: require('@/assets/ma/cnc/3.jpg'),
          desc: 'This process involves blasting the surface with fine glass beads or other media to achieve a smooth, matte, or satin finish. It helps remove tool marks and surface imperfections.',
          roughness: 'Ra 2.8-3.8 µm (depending on the material and blast media used).',
          application: 'Used to achieve an even texture for aesthetic purposes, typically in consumer products or decorative parts.'
        },
        {
          name: 'Anodizing',
           image: require('@/assets/ma/cnc/4.jpg'),
          desc: 'An electrochemical process that increases the thickness of the natural oxide layer on the surface of aluminum parts. It can be either clear (natural) or dyed in various colors. Benefits: Adds corrosion resistance, improves appearance, and can create an electrically insulating surface.',
          roughness: 'Anodizing typically doesn’t significantly change surface roughness.',
          application: 'Commonly used in aerospace, electronics, and automotive industries for aluminum parts.'
        },
        {
          name: 'Polishing',
             image: require('@/assets/ma/cnc/5.jpg'),
          desc: 'A finishing process that makes the surface smooth and glossy by mechanically removing surface material. Polishing can be done to different degrees, from basic smoothing to achieving a mirror-like finish.',
          roughness: 'Can achieve extremely low surface roughness, Ra ≤ 0.1 µm.',
          application: 'Used for aesthetic parts, reflective surfaces, or parts requiring low friction.'
        },
        {
          name: 'Powder Coating',
         image: require('@/assets/ma/cnc/6.png'),
          desc: 'A dry finishing process where powdered paint is electrostatically applied to the surface and then cured under heat to form a durable, uniform coating. It creates a protective layer resistant to wear, corrosion, and environmental factors.',
          roughness: 'Typically smooth but varies depending on the powder type.',
          application: 'Used in automotive, industrial equipment, consumer products, and outdoor applications.'
        },
        {
          name: 'Brushing',
           image: require('@/assets/ma/cnc/7.jpg'),
          desc: 'This finish is created by brushing the surface with abrasive belts or brushes to give it a uniform, directional texture. It provides a satin or matte look while reducing surface imperfections.',
          roughness: 'Ra 0.4-2.5 µm, depending on the type of brush and material.',
          application: 'Common in decorative and aesthetic applications, especially for stainless steel or aluminum products.'
        },
        {
          name: 'Electroplating',
          image: require('@/assets/ma/cnc/8.jpg'),
          desc: 'Uses electric current to reduce dissolved metal cations so that they form a coherent metal coating on an electrode. Common plating metals include chrome, nickel, zinc, gold, silver, etc.',
          roughness: 'Can create very smooth surfaces, depending on plating process and base material.',
          application: 'Improves corrosion resistance, wear resistance, solderability, conductivity, and appearance.'
        },
        {
          name: 'Passivation',
           image: require('@/assets/ma/cnc/9.jpg'),
          desc: 'A chemical treatment applied to stainless steel and other metals to remove free iron from the surface, enhancing corrosion resistance by forming a passive oxide layer.',
          roughness: 'Passivation doesn’t change the surface roughness.',
          application: 'Commonly used in medical, food, and aerospace industries where corrosion resistance is critical.'
        },
        {
          name: 'Heat Treating',
          image: require('@/assets/ma/cnc/10.jpg'),
          desc: 'A process that involves heating and cooling metal parts to change their hardness, strength, or toughness. While not primarily a surface finish, it affects the surface properties by hardening the outer layer.',
          roughness: '',
          application: 'Used for parts that need wear resistance or improved mechanical properties, like gears and cutting tools.'
        },
        {
          name: 'Electropolishing',
          image: require('@/assets/ma/cnc/11.jpg'),
          desc: 'An electrochemical process that removes material from the part’s surface, smoothing and brightening it to achieve a high-gloss, reflective finish. It also improves corrosion resistance and reduces surface roughness.',
          roughness: 'Can reduce surface roughness significantly, up to Ra 0.1 µm.',
          application: 'Used in medical, pharmaceutical, and food processing equipment for cleanability and corrosion resistance.'
        },
        {
          name: 'Chromating',
          image: require('@/assets/ma/cnc/12.jpg'),
          desc: 'A chemical process that converts the surface of metals, particularly aluminum, into a protective layer. Chromate conversion coatings provide corrosion resistance and serve as a primer for paint or powder coating.',
          roughness: 'Typically does not alter surface roughness significantly.',
          application: 'Used in aerospace and electronics industries where corrosion resistance and conductivity are important.'
        },
        {
          name: 'Sandblasting',
         image: require('@/assets/ma/cnc/13.jpg'),
          desc: 'Similar to bead blasting, sandblasting uses abrasive materials (such as silica, sand, or aluminum oxide) to clean or texture the surface. It creates a rougher surface than bead blasting.',
          roughness: 'Ra 2.5-10 µm, depending on the abrasive used.',
          application: 'Prepares surfaces for coating, removes old paint, or creates a rough texture for improved adhesion of coatings.'
        },
        {
          name: 'Tumbling',
          image: require('@/assets/ma/cnc/14.jpg'),
          desc: 'A finishing process that uses abrasive media and vibrations to polish, deburr, or smooth the surface of parts. It’s ideal for small components or complex shapes that need uniform finishing.',
          roughness: 'Varies, but generally produces a smoother finish than raw machined surfaces.',
          application: 'Used for mass finishing in industries like automotive and aerospace for deburring and surface smoothing.'
        },
        {
          name: 'Laser Engraving',
           image: require('@/assets/ma/cnc/15.jpg'),
          desc: 'A process that uses a laser beam to etch or mark the surface of the part with precise, permanent markings. It doesn’t alter the overall finish of the part but adds logos, text, or serial numbers.',
          roughness: '',
          application: 'Common for branding, labeling, and creating unique part identifiers.'
        },
        {
          name: 'Black Oxide',
          image: require('@/assets/ma/cnc/16.jpg'),
          desc: 'A chemical coating applied to steel to create a black matte finish, which provides moderate corrosion resistance and reduces light reflection.',
          roughness: 'Does not significantly change surface roughness.',
          application: 'Used for aesthetic purposes, to reduce glare, and to provide corrosion protection on machine components and tools.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.finishing-options-page {
  padding: 40px 20px;
  background-color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.container {
  /* ✨ 修改点：将 min-width: 1200px 改为 max-width: 1200px，让容器能随屏幕缩小 */
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.page-title {
  /* ✨ 响应式标题：手机上 24px，电脑上 32px */
  font-size: clamp(24px, 4vw, 32px);
  color: #333333;
  text-align: center;
  margin: 0 0 30px;
  font-weight: 600;
}

/* ✨ 核心：表格的外层包裹器 */
.table-wrapper {
  width: 100%;
  margin-bottom: 30px;
  overflow-x: auto; /* 允许横向滑动 */
  -webkit-overflow-scrolling: touch; /* 让 iOS 设备上滑动更顺滑 */
  /* 给表格加个细微边框和阴影，滑动时更有边界感 */
  border: 1px solid #f0ecec; 
  border-radius: 8px;
}

.finishing-table {
  width: 100%;
  /* ✨ 核心：给表格设定一个最小宽度，当屏幕小于800px时，表格不会继续被挤压，而是出现横向滚动条 */
  min-width: 800px; 
  border-collapse: collapse;
  background-color: #ffffff;
}

.finishing-table thead tr {
  background-color: #f0f4f5;
}

.finishing-table th,
.finishing-table td {
  padding: 18px 15px;
  text-align: left;
  color: #666666;
  font-size: 16px;
  line-height: 1.6;
}

.finishing-table th {
  font-weight: 600;
  color: #333333;
  /* 表头文字左对齐更利于阅读长表格 */
  text-align: left; 
}

.finishing-table tbody tr {
  border-top: 1px solid #f0ecec;
}
/* 去除左右外边框，因为外层 wrapper 已经有了 */
.finishing-table tbody tr:first-child {
  border-top: none; 
}

/* 调整列宽分配，使其更合理 */
.finishing-table td:nth-child(1),
.finishing-table th:nth-child(1) {
  width: 100px;
  padding: 12px;
}

.finishing-table td:nth-child(2),
.finishing-table th:nth-child(2) {
  width: 160px;
}

.finishing-table td:nth-child(3),
.finishing-table th:nth-child(3) {
  width: 50%;
}

.finishing-table td:nth-child(4),
.finishing-table th:nth-child(4) {
  width: 25%;
}

.process-img {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.process-link {
  font-weight: 500;
}

.note-bar{
  width: 100%;
}

.note-text {
  margin: 0;
  font-size: 15px;
  color: #666666;
  line-height: 1.6;
}

.contact-link {
  color: #16bc9c;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.contact-link:hover {
  text-decoration: underline;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .finishing-options-page {
    padding: 30px 15px;
  }
  
  .finishing-table th,
  .finishing-table td {
    padding: 12px 10px;
    font-size: 14px; /* 移动端字号稍微调小一点 */
  }
  
  .process-img {
    width: 60px;
    height: 45px;
  }
  
  .note-text {
    font-size: 14px;
  }
}
</style>